import React, { useState, useEffect, useRef } from "react";
import { useFormik, FieldArray, FormikProvider, Form } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import InputField from "../../../../component/input-field";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.css";
import "../../../../css/theme.css";
import { useDispatch, useSelector } from "react-redux";
import { sanitizeInput } from "../../../../utils";
import swal from "@sweetalert/with-react";
import CustomeModal from "../../../../component/modal-popup/customeModal";
import { getAllProductDataList } from "../../../../redux/actions/productMaster";
import {
  addProductionData,
  updateProductionData,
} from "../../../../redux/actions/productionOrder";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import Calendar from "../Calendar";

function CreateProductionOrder() {
  const state = useLocation().state?.update;
  const allSelectedProducts = useLocation().state?.allSelectedProducts;
  const navigate = useNavigate();

  // State
  const [loader, setLoader] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [openCalendarModal, setOpenCalendarModal] = useState(false);
  const [fieldValueToSet, setFieldValueToSet] = useState();
  const [productMasterId, setProductMasterId] = useState();

  //Redux
  const dispatch = useDispatch();
  const { allProductData } = useSelector((state) => state.productMaster);

  // Function to get Product List
  const getProductList = () => {
    const request = {
      onError: (error) => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(getAllProductDataList(request));
  };

  useEffect(() => {
    getProductList();
  }, []);

  useEffect(() => {
    if (allProductData?.length > 0) {
      setProductOptions(
        allProductData.map((item) => {
          const obj = {
            label: item.product_name,
            value: item.product_master_id,
          };
          return obj;
        })
      );
    }
  }, [allProductData]);

  // Function to call POST API to add Production Order
  const addProductionOrder = () => {
    setLoader(true);
    const payload = formik.values;
    payload.planned_date = moment(formik.values.planned_date)
      .add(5, "hours")
      .add(30, "minutes")
      .utc()
      .format();
    payload.actual_date = moment(formik.values.actual_date)
      .add(5, "hours")
      .add(30, "minutes")
      .utc()
      .format();
    const request = {
      payload,
      onSuccess: () => {
        setLoader(false);
        navigate("/order/overview", { state: 3 });
      },
      onError: (error) => {
        setLoader(false);
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    // console.log('payload ---', request.payload)
    dispatch(addProductionData(request));
  };

  // Function to call PATCH API to edit Production Order
  const updateProductionOrder = () => {
    setLoader(true);
    const payload = formik.values;
    payload.planned_date = moment(formik.values.planned_date)
      .add(5, "hours")
      .add(30, "minutes")
      .utc()
      .format();
    payload.actual_date = moment(formik.values.actual_date)
      .add(5, "hours")
      .add(30, "minutes")
      .utc()
      .format();
    const request = {
      production_order_id: state.production_order_id,
      payload,
      onSuccess: () => {
        setLoader(false);
        formik.resetForm();
        navigate("/order/overview", { state: 3 });
      },
      onError: (error) => {
        setLoader(false);
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(updateProductionData(request));
  };

  const validationSchema = Yup.object().shape({
    production_order_reference_no: Yup.string().nullable(),
    actual_date: Yup.string().required("This field is required"),
    planned_date: Yup.string().required("This field is required"),
    products: Yup.array().of(
      Yup.object().shape({
        product_master_id: Yup.string().required("This field is required"),
        qty: Yup.string().required("This field is required"),
      })
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      production_order_reference_no: state?.production_order_reference_no
        ? state?.production_order_reference_no
        : "",
      actual_date: state?.production_order_products[0]?.actual_date
        ? state.production_order_products[0]?.actual_date
        : "",
      planned_date: state?.production_order_products[0]?.planned_date
        ? state.production_order_products[0]?.planned_date
        : "",
      products:
        state?.production_order_products?.length > 0
          ? state.production_order_products.map((item) => {
              const obj = {
                production_order_product_id: item.production_order_product_id,
                product_master_id: item.product_master_id,
                qty: item.qty,
              };
              return obj;
            })
          : allSelectedProducts?.length > 0
          ? allSelectedProducts.map((selected_item) => {
              const product = productOptions.find(
                (item) => item.label === selected_item
              );
              const obj = {
                product_master_id: product ? product.value : "",
                qty: "",
              };
              return obj;
            })
          : [
              {
                product_master_id: "",
                qty: "",
              },
            ],
    },
    validationSchema,
    onSubmit: () => {
      state ? updateProductionOrder() : addProductionOrder();
    },
  });

  const getProductUnit = (id) => {
    const product = allProductData.find(
      (item) => item.product_master_id === id
    );
    return product ? product?.master_uom?.master_uom_name : "";
  };

  return (
    <div className="mt-4 sm:mx-6 lg:mx-8 ">
      <div className="w-full py-1 flex justify-end items-center space-x-4">
        <button
          className="btn bg-white border-slate-200 hover:border-slate-300 text-slate-600 cursor-pointer"
          onClick={() => {
            navigate("/order/overview", { state: 3 });
            formik.resetForm();
          }}
        >
          Cancel
        </button>
        <button
          className="btn bg-[--bg-color-500] hover:bg-[--bg-color-600] text-white cursor-pointer"
          onClick={() => {
            formik.handleSubmit();
          }}
        >
          {loader ? (
            <svg
              className="animate-spin w-5 h-45 fill-current shrink-0"
              viewBox="0 0 16 16"
            >
              <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
            </svg>
          ) : state ? (
            "Update Production Order"
          ) : (
            "Create Production Order"
          )}
        </button>
      </div>
      <div
        className="space-y-6 relative h-screen"
        style={{ height: "calc(100vh - 110px)" }}
      >
        <div className="rounded-md bg-[--bg-color-50] p-4 space-y-6">
          <h4 className="text-[#535860] font-bold leading-[100%] text-base">
            Basic Details
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="w-full">
              <InputField
                label="Production Order Number"
                disabled
                value={state ? state.production_order_no : "TBA"}
              />
            </div>
            <div className="w-full">
              <InputField
                label="Production Order Reference Number"
                name="production_order_reference_no"
                placeholder="Enter Production Order Reference Number"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const value = sanitizeInput(e.target.value);
                  formik.setFieldValue("production_order_reference_no", value);
                }}
                value={
                  formik.values.production_order_reference_no
                    ? formik.values.production_order_reference_no
                    : ""
                }
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="w-full relative">
              <label
                className={`block text-sm font-medium mb-1 leading-3 tracking-tight`}
                htmlFor="card-city"
              >
                Estimated Production Start Date&nbsp;
                <span className="text-rose-500">*</span>
              </label>
              <Flatpickr
                data-enable-time
                options={{
                  altFormat: "j M, Y",
                  dateFormat: "Y-m-d",
                  time_24hr: false,
                  disableMobile: true,
                  enableTime: false,
                  altInput: true,
                  minDate: "today",
                }}
                name="planned_date"
                onBlur={formik.handleBlur}
                value={formik.values.planned_date}
                onChange={(e) => {
                  formik.setFieldValue("planned_date", e[0]);
                }}
                render={({ defaultValue }, ref) => {
                  return (
                    <div className="w-full">
                      <input
                        title="Component Placeholder"
                        className="form-input w-[100%] truncate disabled:border-slate-200"
                        defaultValue={defaultValue}
                        ref={ref}
                        placeholder="Select Estimated Production Start Date"
                      />
                      <div className="absolute inset-0 left-auto !top-8 opacity-100 group">
                        <span className="text-sm text-slate-500 font-medium px-3 ">
                          <span className="far fa-calendar text-[#1E293B] z-60 bg-white"></span>
                        </span>
                      </div>
                      {Boolean(
                        formik.touched.planned_date &&
                          formik.errors.planned_date
                      ) && (
                        <div className="text-xs text-rose-600 pt-1 pl-1">
                          {formik.errors.planned_date}
                        </div>
                      )}
                    </div>
                  );
                }}
              />
            </div>
            <div className="w-full relative">
              <label
                className={`block text-sm font-medium mb-1 leading-3 tracking-tight`}
                htmlFor="card-city"
              >
                Estimated Production End Date&nbsp;
                <span className="text-rose-500">*</span>
              </label>
              <Flatpickr
                data-enable-time
                options={{
                  altFormat: "j M, Y",
                  dateFormat: "Y-m-d",
                  time_24hr: false,
                  disableMobile: true,
                  enableTime: false,
                  altInput: true,
                  minDate: formik.values.planned_date
                    ? formik.values.planned_date
                    : "today",
                }}
                name="actual_date"
                onBlur={formik.handleBlur}
                value={formik.values.actual_date}
                onChange={(e) => {
                  formik.setFieldValue("actual_date", e[0]);
                }}
                render={({ defaultValue }, ref) => {
                  return (
                    <div className="w-full">
                      <input
                        title="Component Placeholder"
                        className="form-input w-[100%] truncate disabled:border-slate-200"
                        defaultValue={defaultValue}
                        ref={ref}
                        placeholder="Select Estimated Production End Date"
                      />
                      <div className="absolute inset-0 left-auto !top-8 opacity-100 group">
                        <span className="text-sm text-slate-500 font-medium px-3 ">
                          <span className="far fa-calendar text-[#1E293B] z-60 bg-white"></span>
                        </span>
                      </div>
                      {Boolean(
                        formik.touched.actual_date && formik.errors.actual_date
                      ) && (
                        <div className="text-xs text-rose-600 pt-1 pl-1">
                          {formik.errors.actual_date}
                        </div>
                      )}
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </div>

        <div
          className="rounded-md bg-[--bg-color-50] p-4 space-y-6 h-screen overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          style={{ height: "calc(100vh - 435px)" }}
        >
          <h4 className="text-[#535860] font-bold leading-[100%] text-base">
            Ordered Products
          </h4>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate>
              <FieldArray
                name="products"
                render={(arrayHelpers) => (
                  <div className="space-y-2">
                    {formik.values.products.map((item, index) => {
                      return (
                        <div
                          key={index}
                          className="grid grid-cols-2 gap-2 relative"
                        >
                          <div className="w-full">
                            {!index && (
                              <label
                                className={`block text-sm font-medium mb-1 leading-3 tracking-tight`}
                                htmlFor="card-city"
                              >
                                Product&nbsp;
                                <span className="text-rose-500">*</span>
                              </label>
                            )}

                            <Select
                              className="basic-single text-sm unresetText"
                              menuPlacement="top"
                              menuPortalTarget={document.body}
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  backgroundColor: "white",
                                  borderColor: "lightgray",
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                placeholder: (provided, state) => ({
                                  ...provided,
                                  color: "#9297A2",
                                  fontSize: "14px",
                                }),
                              }}
                              classNames={{
                                control: (style) => {
                                  return `!border !shadow-sm ${
                                    style.isFocused
                                      ? "!border-[--bg-color-300]/50"
                                      : "!border-slate-200 hover:!border-gray-300"
                                  }`;
                                },
                                container: (style) => {
                                  return "!h-[37.5px] !font-inter !text-sm";
                                },
                                menuList: (style) => {
                                  return `!max-h-[200px]`;
                                },
                              }}
                              onBlur={formik.handleBlur}
                              onChange={(product) => {
                                formik.setFieldValue(
                                  `products[${index}].product_master_id`,
                                  product.value
                                );
                              }}
                              value={
                                formik.values.products[index].product_master_id
                                  ? productOptions.find(
                                      (element) =>
                                        element.value ===
                                        formik.values.products[index]
                                          .product_master_id
                                    )
                                  : ""
                              }
                              options={productOptions.filter((optionItem) => {
                                return !formik.values.products.some(
                                  (el) =>
                                    el?.product_master_id === optionItem.value
                                );
                              })}
                              placeholder="Select Product"
                            />
                            {Boolean(
                              formik?.touched?.products?.[index]
                                ?.product_master_id &&
                                formik?.errors?.products?.[index]
                                  ?.product_master_id
                            ) && (
                              <div className="text-xs text-rose-600 pt-1 pl-1">
                                {
                                  formik?.errors?.products?.[index]
                                    ?.product_master_id
                                }
                              </div>
                            )}
                          </div>
                          <div className="w-full flex items-center space-x-2">
                            <InputField
                              label={!index && "Quantity"}
                              placeholder="Enter Quantity"
                              required
                              name="qty"
                              onBlur={formik.handleBlur}
                              onChange={(e) => {
                                const value = sanitizeInput(e.target.value);
                                formik.setFieldValue(
                                  `products[${index}].qty`,
                                  value
                                );
                              }}
                              value={formik.values.products[index].qty}
                              error={
                                Boolean(
                                  formik?.touched?.products?.[index]?.qty &&
                                    formik?.errors?.products?.[index]?.qty
                                )
                                  ? formik?.errors?.products?.[index]?.qty
                                  : null
                              }
                              suffix={
                                getProductUnit(
                                  formik.values.products[index]
                                    .product_master_id
                                ) ? (
                                  getProductUnit(
                                    formik.values.products[index]
                                      .product_master_id
                                  )
                                ) : (
                                  <span title="Delete" className="fa-solid fa-trash-can mt-6 text-rose-500 cursor-pointer invisible"></span>
                                )
                              }
                            />

                            {/* <div
                              className={`border border-[#DDDDDD] bg-white rounded-[5px] py-1.5 px-2.5 ${
                                !index && "mt-6"
                              } ${
                                !formik.values.products[index].product_master_id
                                  ? "pointer-events-none bg-slate-200"
                                  : "bg-white cursor-pointer"
                              }`}
                              onClick={() => {
                                setProductMasterId(
                                  formik.values.products[index].product_master_id
                                );
                                setFieldValueToSet(`products[${index}].qty`);
                                setOpenCalendarModal(true);
                              }}
                            >
                              <span className="fa-regular fa-calendar"></span>
                            </div> */}

                            <div className="flex justify-start items-center">
                              {formik.values.products.length > 1 && (
                                <span
                                  className={`fa-solid fa-trash-can ${
                                    !index && "mt-6"
                                  } text-rose-500 cursor-pointer`}
                                  onClick={() => arrayHelpers.remove(index)}
                                ></span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    <h6
                      className={`text-xs font-semibold leading-3 tracking-tight text-[--bg-color-500] cursor-pointer my-2`}
                      onClick={() => {
                        arrayHelpers.push({
                          product_master_id: "",
                          qty: "",
                        });
                      }}
                    >
                      + Add Item
                    </h6>
                  </div>
                )}
              />
            </Form>
          </FormikProvider>
        </div>
      </div>
      <Calendar
        openCalendarModal={openCalendarModal}
        setOpenCalendarModal={setOpenCalendarModal}
        productMasterId={productMasterId}
        setProductMasterId={setProductMasterId}
        formik={formik}
        fieldValueToSet={fieldValueToSet}
        setFieldValueToSet={setFieldValueToSet}
      />
    </div>
  );
}

CreateProductionOrder.auth = {
  path: "/order/create_production_order",
  role: "super-admin admin",
};

export default CreateProductionOrder;
